import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Eye, Trash2, FolderOpen, Calendar } from 'lucide-react';
import { toast } from 'sonner';

export const Projects = () => {
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('active'); // 'active' or 'completed'
  const [editingNotes, setEditingNotes] = useState(false);
  const [noteText, setNoteText] = useState('');

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = () => {
    const stored = localStorage.getItem('projects');
    if (stored) {
      setProjects(JSON.parse(stored));
    }
  };

  const saveProjects = (updatedProjects) => {
    localStorage.setItem('projects', JSON.stringify(updatedProjects));
    setProjects(updatedProjects);
  };

  const deleteProject = (id) => {
    if (window.confirm('Bu projeyi silmek istediğinizden emin misiniz?')) {
      const updatedProjects = projects.filter(p => p.id !== id);
      saveProjects(updatedProjects);
      toast.success('Proje silindi');
    }
  };

  const toggleProjectStatus = (id) => {
    const updatedProjects = projects.map(p => {
      if (p.id === id) {
        const newStatus = p.status === 'active' ? 'completed' : 'active';
        return { ...p, status: newStatus };
      }
      return p;
    });
    saveProjects(updatedProjects);
    toast.success('Proje durumu güncellendi');
  };

  const updateProjectNotes = (id, notes) => {
    const updatedProjects = projects.map(p => {
      if (p.id === id) {
        return { ...p, notes };
      }
      return p;
    });
    saveProjects(updatedProjects);
    setSelectedProject({ ...selectedProject, notes });
    toast.success('Not güncellendi');
  };

  const viewProject = (project) => {
    setSelectedProject(project);
    setNoteText(project.notes || '');
    setEditingNotes(false);
    setIsViewDialogOpen(true);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const activeProjects = projects.filter(p => (p.status || 'active') === 'active');
  const completedProjects = projects.filter(p => p.status === 'completed');
  const displayProjects = activeTab === 'active' ? activeProjects : completedProjects;

  return (
    <div className="p-8 animate-in">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-foreground mb-2">Projeler</h1>
        <p className="text-muted-foreground text-lg">Kayıtlı maliyet hesaplamalarınız</p>
      </div>

      {/* Projects List */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <FolderOpen className="h-5 w-5 text-primary" />
                Proje Listesi
              </CardTitle>
              <CardDescription>
                {activeProjects.length} aktif, {completedProjects.length} tamamlanmış proje
              </CardDescription>
            </div>
          </div>
          
          {/* Tabs */}
          <div className="flex gap-2 mt-4 border-b border-border">
            <button
              onClick={() => setActiveTab('active')}
              className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 ${
                activeTab === 'active'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              Aktif Projeler ({activeProjects.length})
            </button>
            <button
              onClick={() => setActiveTab('completed')}
              className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 ${
                activeTab === 'completed'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              Tamamlananlar ({completedProjects.length})
            </button>
          </div>
        </CardHeader>
        <CardContent>
          {displayProjects.length === 0 ? (
            <div className="text-center py-12">
              <FolderOpen className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground mb-2">
                {activeTab === 'active' ? 'Henüz aktif proje bulunmuyor' : 'Henüz tamamlanmış proje bulunmuyor'}
              </p>
              <p className="text-sm text-muted-foreground">Hesaplama sayfasından yeni bir proje oluşturun</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Proje Adı</TableHead>
                    <TableHead>Kalem Sayısı</TableHead>
                    <TableHead className="text-right">Toplam Maliyet</TableHead>
                    <TableHead className="text-right">Kalan Ödeme</TableHead>
                    <TableHead>Tarih</TableHead>
                    <TableHead className="text-right">İşlemler</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {displayProjects.map((project) => (
                    <TableRow key={project.id}>
                      <TableCell>
                        <div>
                          <div className="font-medium">{project.name}</div>
                          {project.notes && (
                            <div className="text-xs text-muted-foreground mt-1 line-clamp-1">
                              {project.notes}
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary">{project.items.length} kalem</Badge>
                      </TableCell>
                      <TableCell className="text-right font-semibold text-foreground">
                        ₺{project.totalCost.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </TableCell>
                      <TableCell className="text-right">
                        {project.remainingPayment !== undefined ? (
                          <span className={`font-semibold ${project.remainingPayment > 0 ? 'text-accent' : 'text-success'}`}>
                            ₺{project.remainingPayment.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 text-muted-foreground text-sm">
                          <Calendar className="h-4 w-4" />
                          {formatDate(project.createdAt)}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex gap-2 justify-end">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => viewProject(project)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => toggleProjectStatus(project.id)}
                            className={project.status === 'completed' ? 'text-success' : 'text-primary'}
                          >
                            {project.status === 'completed' ? '✓' : '○'}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteProject(project.id)}
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* View Project Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <div>
                <DialogTitle>{selectedProject?.name}</DialogTitle>
                <DialogDescription>
                  Oluşturulma: {selectedProject && formatDate(selectedProject.createdAt)}
                </DialogDescription>
              </div>
              {selectedProject && (
                <Button
                  onClick={() => toggleProjectStatus(selectedProject.id)}
                  variant={selectedProject.status === 'completed' ? 'outline' : 'default'}
                  size="sm"
                  className={selectedProject.status === 'completed' ? '' : 'bg-accent hover:bg-accent/90'}
                >
                  {selectedProject.status === 'completed' ? 'Aktif Yap' : 'Tamamlandı Olarak İşaretle'}
                </Button>
              )}
            </div>
          </DialogHeader>
          
          {selectedProject && (
            <div className="space-y-6">
              {/* Notes Section */}
              {(selectedProject.notes || editingNotes) && (
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base">Proje Notları</CardTitle>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          if (editingNotes) {
                            updateProjectNotes(selectedProject.id, noteText);
                          }
                          setEditingNotes(!editingNotes);
                        }}
                      >
                        {editingNotes ? 'Kaydet' : 'Düzenle'}
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {editingNotes ? (
                      <Input
                        value={noteText}
                        onChange={(e) => setNoteText(e.target.value)}
                        placeholder="Proje notları..."
                      />
                    ) : (
                      <p className="text-sm text-muted-foreground">{selectedProject.notes}</p>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Summary */}
              <Card className="bg-gradient-to-br from-primary/5 to-accent/5 border-none">
                <CardContent className="pt-6">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Toplam Kalem</p>
                      <p className="text-2xl font-bold text-foreground">{selectedProject.items.length}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Toplam Maliyet</p>
                      <p className="text-2xl font-bold text-foreground">
                        ₺{selectedProject.totalCost.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Ön Ödeme</p>
                      <p className="text-2xl font-bold text-primary">
                        ₺{(selectedProject.advancePayment || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Kalan Ödeme</p>
                      <p className="text-2xl font-bold text-accent">
                        ₺{(selectedProject.remainingPayment || selectedProject.totalCost).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Items */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Malzemeler</h3>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Tip</TableHead>
                      <TableHead>Ad</TableHead>
                      <TableHead>Kategori</TableHead>
                      <TableHead className="text-right">Miktar</TableHead>
                      <TableHead className="text-right">Birim Fiyat</TableHead>
                      <TableHead className="text-right">Toplam</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedProject.items.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell>
                          <Badge variant={item.type === 'labor' ? 'default' : 'outline'}>
                            {item.type === 'labor' ? 'İşçilik' : 'Malzeme'}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-medium">{item.materialName}</TableCell>
                        <TableCell>
                          <Badge variant="secondary">{item.category}</Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          {item.quantity} {item.unit}
                        </TableCell>
                        <TableCell className="text-right">
                          ₺{item.pricePerUnit.toFixed(2)}
                        </TableCell>
                        <TableCell className="text-right font-semibold text-primary">
                          ₺{item.totalCost.toFixed(2)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Category Breakdown */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Kategoriye Göre Dağılım</h3>
                <div className="grid gap-3">
                  {Object.entries(
                    selectedProject.items.reduce((acc, item) => {
                      if (!acc[item.category]) {
                        acc[item.category] = { count: 0, total: 0 };
                      }
                      acc[item.category].count++;
                      acc[item.category].total += item.totalCost;
                      return acc;
                    }, {})
                  ).map(([category, data]) => (
                    <div key={category} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                      <div className="flex items-center gap-3">
                        <Badge>{category}</Badge>
                        <span className="text-sm text-muted-foreground">{data.count} kalem</span>
                      </div>
                      <span className="font-semibold text-primary">
                        ₺{data.total.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Projects;